/**
 * Materialized run-state snapshots. The daemon folds each run's events into its materialized
 * state (@viko/transcript-ui `reduceMaterial`) and publishes a sealed snapshot tagged with the
 * event seq it covers; the browser renders it at once and applies only the events after it.
 *
 * AEAD context: `{runId, objectId: "snapshot:<runId>", seq: covered seq, kind: "run.snapshot"}`
 * under the account data key, so the relay can neither read, edit nor move a snapshot to another
 * run or seq. The hub keeps only the newest snapshot per run and never lets it go backwards.
 */
import { z } from 'zod';
import { EncryptedContent } from './content.js';
export const RUN_SNAPSHOT_KIND = 'run.snapshot';
/** Ciphertext envelope bound (a mega run's material is ~25 KB of plaintext). */
export const RUN_SNAPSHOT_MAX_BYTES = 192_000;
/** Runs per browser batch read. */
export const RUN_SNAPSHOT_BATCH = 64;
const RunId = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9:_.-]+$/)
    .refine((id) => !['__proto__', 'constructor', 'prototype'].includes(id));
const RpcId = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9:_-]+$/);
const Seq = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const runSnapshotObjectId = (runId) => `snapshot:${runId}`;
export const RunSnapshotContent = EncryptedContent.refine((c) => c.context.kind === RUN_SNAPSHOT_KIND &&
    RunId.safeParse(c.context.runId).success &&
    c.context.objectId === runSnapshotObjectId(c.context.runId), 'invalid run snapshot context');
/** Sealed plaintext; `runId`/`seq` must repeat the authenticated context. */
export const RunSnapshotPlaintext = z.strictObject({
    version: z.literal(1),
    runId: RunId,
    seq: Seq,
    material: z.record(z.string(), z.unknown()),
});
/** Daemon → hub (rpc): store the newest snapshot of a run this machine owns. */
export const RunSnapshotPutRpc = z.strictObject({
    type: z.literal('rpc'),
    id: RpcId,
    method: z.literal('content.snapshot.put'),
    params: z.strictObject({ snapshot: RunSnapshotContent }),
});
/** Daemon → hub (rpc): read back its own run's ciphertext events (snapshot backfill). */
export const RunEventsReadRpc = z.strictObject({
    type: z.literal('rpc'),
    id: RpcId,
    method: z.literal('content.events.read'),
    params: z.strictObject({ runId: RunId, after: Seq }),
});
/** Browser → hub: the newest snapshot of each run (list surfaces batch these). */
export const RunSnapshotGetFrame = z.strictObject({
    type: z.literal('content.snapshot.get'),
    reqId: RpcId,
    runIds: z.array(RunId).min(1).max(RUN_SNAPSHOT_BATCH),
});
export const RunSnapshotBatchFrame = z.strictObject({
    type: z.literal('content.snapshot.batch'),
    reqId: RpcId,
    snapshots: z.array(RunSnapshotContent).max(RUN_SNAPSHOT_BATCH),
    /** Head seq the hub holds for each requested run (only runs it knows). */
    heads: z.record(z.string(), Seq),
});
/** Hub → browsers: a run's snapshot advanced (small; list surfaces re-render from it). */
export const RunSnapshotPushFrame = z.strictObject({
    type: z.literal('content.snapshot'),
    snapshot: RunSnapshotContent,
});
/** Browser → hub: a bounded page of a run's ciphertext events after `after` (older turns). */
export const RunEventsReadFrame = z.strictObject({
    type: z.literal('content.events.read'),
    reqId: RpcId,
    runId: RunId,
    after: Seq,
});
export const RunEventsPageFrame = z.strictObject({
    type: z.literal('content.events.page'),
    reqId: RpcId,
    runId: RunId,
    events: z.array(EncryptedContent).max(64),
    more: z.boolean(),
});
//# sourceMappingURL=run-snapshot.js.map